import React, {CSSProperties, FC} from "react";
import {Checkbox, Link} from "@material-ui/core";
import ErrorIcon from "@material-ui/icons/Error";

import StatusBadge from "./status_badge";
import {getClauseChangeScrollTarget} from "utils/amendments/get_amendment_anchor_text";
import formatClauseLabel from "utils/amendments/format_clause_label";
import type {ScrollToAmendmentClause} from "./amendment_group";
import {ProcessedClauseChange} from "common/flowmaster/types/amendment";

const styles: Record<string, CSSProperties> = {
  summaryItem: {
    color: "#666",
    fontSize: "0.9rem",
    marginBottom: "8px",
    lineHeight: 1.4,
  },
  summaryClauseRef: {
    fontWeight: 600,
    color: "#333",
  },
  summaryActionType: {
    fontWeight: 500,
    color: "#888",
    fontStyle: "italic",
  },
  summaryHeading: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    columnGap: "6px",
  },
  summaryEditCount: {
    color: "#999",
    fontSize: "0.8rem",
    fontWeight: 500,
  },
  summarySubList: {
    margin: "4px 0 0 0",
    padding: "0 0 0 14px",
    listStyleType: "circle",
  },
  summarySubItem: {
    color: "#666",
    fontSize: "0.9rem",
    marginBottom: "4px",
    lineHeight: 1.4,
  },
  errorMessage: {
    color: "#d32f2f",
    fontSize: "0.85rem",
    display: "flex",
    alignItems: "flex-start",
    gap: "4px",
    marginTop: "4px",
  },
  errorIcon: {
    fontSize: "14px",
    color: "#d32f2f",
  },
};

function getActionTypeLabel(change: ProcessedClauseChange) {
  if (change.delete) {
    return "Delete";
  }
  if (change.insert_before) {
    return "Insert before";
  }
  if (change.insert_after) {
    return "Insert after";
  }
  return "Modify";
}

interface SummaryGroupProps {
  change: ProcessedClauseChange;
  fullyApplied: boolean;
  noneApplied: boolean;
  isPartiallyApplied: boolean;
  isSelected: boolean;
  isAnyBulkApplyActive: boolean;
  isChangeApplied: (changeId: string) => boolean;
  onSelectChange: (checked: boolean) => void;
  errorMessage?: string;
  scrollToClause?: ScrollToAmendmentClause;
}

const SummaryGroup: FC<SummaryGroupProps> = ({
  change,
  fullyApplied,
  noneApplied,
  isPartiallyApplied,
  isSelected,
  isAnyBulkApplyActive,
  isChangeApplied,
  onSelectChange,
  errorMessage,
  scrollToClause,
}) => {
  const edits =
    change.grouped && change.related_changes
      ? change.related_changes
      : [change];
  const partialClause = !fullyApplied && !noneApplied;
  const scrollTarget = getClauseChangeScrollTarget(change);
  const clauseRefLabel = formatClauseLabel(
    change.clause_reference,
    change.section_reference,
    false,
  );
  return (
    <li style={styles.summaryItem}>
      <span style={styles.summaryHeading}>
        <Checkbox
          checked={isSelected}
          onChange={e => onSelectChange(e.target.checked)}
          disabled={fullyApplied || isAnyBulkApplyActive}
          style={{padding: "0px"}}
          inputProps={{"aria-label": `Select amendments for ${clauseRefLabel}`}}
        />
        {scrollToClause && scrollTarget ? (
          <Link
            component="button"
            type="button"
            color="primary"
            onClick={() => scrollToClause(scrollTarget)}
            style={{fontWeight: 600, textAlign: "left"}}
          >
            {clauseRefLabel}
          </Link>
        ) : (
          <span style={styles.summaryClauseRef}>{clauseRefLabel}</span>
        )}
        {edits.length > 1 && (
          <span style={styles.summaryEditCount}>({edits.length} edits)</span>
        )}
        {fullyApplied && <StatusBadge applied />}
        {noneApplied && isPartiallyApplied && <StatusBadge applied={false} />}
      </span>
      <ul style={styles.summarySubList}>
        {edits.map(edit => (
          <li key={edit.id} style={styles.summarySubItem}>
            <span style={styles.summaryActionType}>
              {getActionTypeLabel(edit)}
            </span>
            {partialClause && (
              <StatusBadge
                applied={isChangeApplied(edit.id)}
                style={{marginLeft: "6px"}}
              />
            )}
            {edit.rawAmendment?.rationale && (
              <>
                {": "}
                {edit.rawAmendment.rationale}
              </>
            )}
          </li>
        ))}
      </ul>
      {errorMessage && (
        <div style={styles.errorMessage}>
          <ErrorIcon style={styles.errorIcon} />
          {errorMessage}
        </div>
      )}
    </li>
  );
};

export default SummaryGroup;
